import get from 'lodash.get';
import { createSelector } from 'reselect';
import type { RootState } from '../../../store/types';

export const getNotifications = (state: RootState) => state.notificationSettings;

export const getTCPAelection = createSelector(getNotifications, (notificationSettings) => {
  return (
    notificationSettings.entities?.content?.general?.tcpa ?? {
      isOptedIn: false,
      isExplicitConsent: false
    }
  );
});

export const getEmailElections = createSelector(getNotifications, (notificationSettings) => {
  return (
    notificationSettings.entities?.content?.email ?? {
      emailAlerts: false,
      emailEvents: false,
      emailGlobal: false,
      emailOffers: false,
      emailPromotions: false
    }
  );
});

export const getSoftpullConsent = createSelector(getNotifications, (notificationSettings) => {
  const isSoftpullConsent = notificationSettings.softpullConsent?.isOptedIn;
  return isSoftpullConsent;
});

export const getIsSubscribedToAlerts = createSelector(getEmailElections, (emailElections) => {
  return emailElections.emailAlerts;
});

export const getIsSubscribedToEvents = createSelector(getEmailElections, (emailElections) => {
  return emailElections.emailEvents;
});

export const getIsSubscribedToPromotions = createSelector(getEmailElections, (emailElections) => {
  return emailElections.emailPromotions;
});

export const getIsSubscribedToOffers = createSelector(getEmailElections, (emailElections) => {
  return emailElections.emailOffers;
});

export const getIsSubscribedGlobal = createSelector(getEmailElections, (emailElections) => {
  return emailElections.emailGlobal;
});

export const getIsSubscribedToTCPA = createSelector(getTCPAelection, (TCPAElections) => {
  return TCPAElections.isOptedIn;
});

export const getHasFetchedNotifications = createSelector(
  getNotifications,
  (notificationSettings) => {
    const hasFetchedNotifications = get(notificationSettings, 'notifications.hasFetched', false);

    return hasFetchedNotifications;
  }
);

export const getHasFetchedSoftpullConsent = createSelector(
  getNotifications,
  (notificationSettings) => {
    const hasFetchedSoftpullConsent = get(
      notificationSettings,
      'softpullConsent.hasFetched',
      false
    );

    return hasFetchedSoftpullConsent;
  }
);

export const getIsUpdatingEmailNotifications = createSelector(
  getNotifications,
  (notificationSettings) => {
    const isUpdatingEmailNotifications = get(
      notificationSettings,
      'updateEmailNotifications.isLoading',
      false
    );

    return isUpdatingEmailNotifications;
  }
);

export const getIsUpdatingSoftpullConsent = createSelector(
  getNotifications,
  (notificationSettings) => {
    const isUpdatingSoftpullConsent =
      get(notificationSettings, 'softpullConsent.isLoading', false) &&
      get(notificationSettings, 'softpullConsent.hasFetched', false);

    return isUpdatingSoftpullConsent;
  }
);

export const getIsUpdatingTCPANotifications = createSelector(
  getNotifications,
  (notificationSettings) => {
    const isUpdatingTCPANotifications = get(
      notificationSettings,
      'updateTCPANotifications.isLoading',
      false
    );

    return isUpdatingTCPANotifications;
  }
);

export const getIsUpdatingNotifications = createSelector(
  getIsUpdatingEmailNotifications,
  getIsUpdatingTCPANotifications,
  (isUpdatingEmailNotifications, isUpdatingTCPANotifications) =>
    isUpdatingEmailNotifications || isUpdatingTCPANotifications
);

export const getIsLoadingNotifications = createSelector(
  getNotifications,
  (notificationSettings) => {
    const isLoadingNotifications = get(notificationSettings, 'notifications.isLoading', true);

    return isLoadingNotifications;
  }
);

export const getIsLoadingSoftpullConsent = createSelector(
  getNotifications,
  (notificationSettings) => {
    const isLoadingSoftpullConsent = get(notificationSettings, 'softpullConsent.isLoading', false);
    return isLoadingSoftpullConsent;
  }
);

export const getNotificationsError = createSelector(getNotifications, (notificationSettings) => {
  const notificationsError = get(notificationSettings, 'notifications.error');

  return notificationsError;
});

export const getSoftpullConsentError = createSelector(getNotifications, (notificationSettings) => {
  const softpullConsentError = get(notificationSettings, 'softpullConsent.error');
  return softpullConsentError;
});
